import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import { Button, Grid2, TextField } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { RejectedBillet } from "../models/RejectedBillet";
import { updateBilletCount } from "../repositories";

type Props = {
	row: RejectedBillet | null;
	shiftId: number | undefined;
	onHide: () => void;
};

export const EditBilletCountModal = ({ row, shiftId, onHide }: Props) => {
	const [billetCount, setBilletCount] = useState<number | "">(
		row?.BilletCount ?? 1,
	);
	const [comments, setComments] = useState<string>(row?.Comments ?? "");
	const [error, setError] = useState("");
	const [notificationMessage, setNotificationMessage] = useState("");

	const queryClient = useQueryClient();

	const mutation = useMutation(updateBilletCount, {
		onSuccess: async () => {
			setNotificationMessage("Billet count updated successfully");
			await queryClient.invalidateQueries(["rejectedBillets", shiftId]);
			onHide();
		},
		onError: (error: Error) => {
			setError(error.message);
		},
	});

	// Sync local state when a different row is opened
	useEffect(() => {
		if (row) {
			setBilletCount(row.BilletCount);
			setComments(row.Comments ?? "");
		}
	}, [row?.RejectedBilletId]);

	return (
		<>
			<MesfModal
				title="EDIT BILLET COUNT"
				open={row !== null}
				id="edit-billet-count-modal"
				handleClose={() => onHide()}
			>
				<MesfModal.Content dividers style={{ padding: "15px 30px" }}>
					<Grid2 container spacing={1}>
						<Grid2 size={{ md: 12, xs: 12 }}>
							<TextField
								label="Billet Count"
								type="number"
								fullWidth
								value={billetCount}
								onChange={(e) => {
									if (e.target.value === "") {
										setBilletCount("");
										return;
									}
									const val = Number.parseInt(e.target.value, 10);
									if (!Number.isNaN(val) && val >= 0) {
										setBilletCount(val);
									}
								}}
								inputProps={{ min: 0 }}
							/>
						</Grid2>
						<Grid2 size={{ md: 12, xs: 12 }}>
							<TextField
								label="Comments"
								fullWidth
								multiline
								minRows={2}
								value={comments}
								onChange={(e) => setComments(e.target.value)}
							/>
						</Grid2>
					</Grid2>
				</MesfModal.Content>
				<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
					<Grid2 container spacing={2} justifyContent="flex-end">
						<Grid2 size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
							<Button
								fullWidth
								variant="contained"
								color="inherit"
								onClick={() => onHide()}
								disabled={mutation.isLoading}
							>
								Cancel
							</Button>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 4 }}>
							<Button
								fullWidth
								variant="contained"
								color="primary"
								disabled={mutation.isLoading || !row || billetCount === ""}
								onClick={() => {
									if (row) {
										mutation.mutate({
											rejectedBilletId: row.RejectedBilletId,
											billetCount: billetCount as number,
											comments,
										});
									}
								}}
							>
								Confirm
							</Button>
						</Grid2>
					</Grid2>
				</MesfModal.Actions>
			</MesfModal>
			<ErrorModal error={error} onHide={() => setError("")} />
			<NotificationSnackBar
				message={notificationMessage}
				onHide={() => setNotificationMessage("")}
			/>
		</>
	);
};
